import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";

import {
    BulkResponse, BulkUtil, CreateRequest, GetResponse, RequestUtil, SearchRequest,
    SearchResponse, UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    Campaign, CampaignOperation, CampaignSearchFilterOptions
} from "@shopliftr/common-js/smart";
import {
    ModelAction, ISearchSort
} from "@shopliftr/common-js/data";
import {
    ICreateInternalApiClientService, IUpdateInternalApiClientService, InternalApiRequestService,
    InvalidParametersException, ListUtil, IQueryInternalApiClientService
} from "@shopliftr/common-ng";

/**
 * API Client Service for managing campaign operations through internal API endpoints.
 *
 * **Service Pattern:** API Client Service
 *
 * **Responsibilities:**
 * - Encapsulates campaign API endpoint paths
 * - Builds typed requests using RequestUtil
 * - Validates parameters before making requests
 * - Maps responses to Campaign domain models
 * - Provides CRUD operations (create, bulkCreate, get, bulkGet, getAll, search, update, bulkUpdate, updateWithChanges)
 * - Provides lifecycle operations (archive, restore)
 *
 * **Provision:** Singleton service provided in 'root'
 *
 * @implements {ICreateInternalApiClientService<Campaign>} Standard internal API create client pattern
 * @implements {IUpdateInternalApiClientService<Campaign>} Standard internal API update client pattern
 * @implements {IQueryInternalApiClientService<Campaign>} Standard internal API query client pattern
 *
 * @example
 * ```typescript
 * // Inject the service
 * private readonly _campaignApiClientService = inject(CampaignApiClientService);
 *
 * // Get a campaign
 * this._campaignApiClientService.get$('campaign-id').subscribe(campaign => {
 *   console.log(campaign.name);
 * });
 *
 * // Search campaigns
 * const filterOptions = new CampaignSearchFilterOptions();
 * this._campaignApiClientService.search$(filterOptions, 0, 10).subscribe(response => {
 *   console.log(response.results);
 * });
 * ```
 *
 * @see {@link ICreateInternalApiClientService} for the create API client interface
 * @see {@link IUpdateInternalApiClientService} for the update API client interface
 * @see {@link IQueryInternalApiClientService} for the query API client interface
 * @see {@link CampaignListDataService} for list data service functionality
 */
@Injectable({
    providedIn: "root"
})
export class CampaignApiClientService implements ICreateInternalApiClientService<Campaign>, IUpdateInternalApiClientService<Campaign>,
IQueryInternalApiClientService<Campaign> {
    /**
     * Base API path for campaign endpoints.
     * Used as the root path for all campaign-related API requests.
     *
     * @readonly
     * @example 'campaign' -> '/api/campaign/{id}'
     */
    readonly basePath = "campaign";

    /**
     * Internal API request service for making HTTP calls to the backend.
     * Injected using Angular's inject() function.
     *
     * @private
     * @readonly
     */
    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Archives a campaign by setting its status to archived.
     *
     * @param id The unique identifier of the campaign to archive
     * @param version The current version of the campaign for optimistic locking
     * @returns Observable that completes when the campaign is successfully archived
     * @throws {InvalidParametersException} When id is not provided
     */
    archive$(id: string, version: number): Observable<void> {

        if (!id) {
            throw new InvalidParametersException("id for the archived campaign is required");
        }

        const request = RequestUtil.createCustomOpRequest(
            id, Campaign.modelName, CampaignOperation.Archive, undefined, version
        );

        return this._internalApiRequestService.customOp$(request, this.basePath).pipe(map(() => {}));
    }

    /**
     * Retrieves multiple campaigns by their unique identifiers in a single bulk request.
     *
     * @param ids Array of unique identifiers of the campaigns to retrieve
     * @returns Observable that emits a BulkResponse containing the requested campaigns
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Campaign>(id, Campaign.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Creates multiple campaigns in a single bulk request.
     *
     * @param items Array of create requests for campaigns to create
     * @returns Observable that emits a BulkResponse with the results of each create operation
     */
    bulkCreate$(items: Array<CreateRequest<Campaign, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Updates multiple campaigns in a single bulk request.
     *
     * @param requests Array of update requests for campaigns to update
     * @returns Observable that emits a BulkResponse with the results of each update operation
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieves a single campaign by its unique identifier.
     *
     * @param id The unique identifier of the campaign to retrieve
     * @returns Observable that emits the campaign data
     * @throws Observable error if campaign is not found or request fails
     */
    get$(id: string): Observable<Campaign> {

        const request = RequestUtil.createGetRequest<void>(
            id, Campaign.modelName, ModelAction.ClientGet
        );

        return this._internalApiRequestService.get$(request, this.basePath).pipe(
            map((response: GetResponse<Campaign>) => response.payload)
        );
    }

    /**
     * Retrieves all campaigns matching the provided filter criteria without pagination.
     *
     * @param filterOptions The search criteria to apply
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable that emits an array of all matching campaigns
     */
    getAll$(filterOptions: CampaignSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Campaign>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch, Campaign.modelName, undefined, undefined, filterOptions, sort
        );
        return this._internalApiRequestService.getAll$<Campaign, CampaignSearchFilterOptions>(request, this.basePath);
    }

    /**
     * Searches for campaigns using the provided filter criteria.
     *
     * Supports advanced filtering, pagination, and sorting capabilities.
     * Returns both the matching results and total count for pagination.
     *
     * @param filterOptions The search criteria to apply
     * @param page Zero-based page index for pagination (optional)
     * @param pageSize Number of items per page (optional)
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable that emits search results with pagination metadata
     */
    search$(
        filterOptions: CampaignSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Campaign>> {

        const request: SearchRequest<CampaignSearchFilterOptions> = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Campaign.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$(request, this.basePath);
    }

    /**
     * Creates a new campaign in the system.
     *
     * @param campaign The campaign data to create
     * @returns Observable that emits the created campaign with assigned ID and metadata
     */
    create$(campaign: Campaign): Observable<Campaign> {

        const request = RequestUtil.createCreateRequest<Campaign, void>(
            ModelAction.ClientCreate, campaign
        );

        return this._internalApiRequestService.create$(request, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Restores an archived campaign back to active status.
     *
     * @param id The unique identifier of the campaign to restore
     * @param version The current version of the campaign for optimistic locking
     * @returns Observable that completes when the campaign is successfully restored
     * @throws {InvalidParametersException} When id is not provided
     */
    restore$(id: string, version: number): Observable<void> {

        if (!id) {
            throw new InvalidParametersException("id for the restored campaign is required");
        }

        const request = RequestUtil.createCustomOpRequest(
            id, Campaign.modelName, CampaignOperation.Restore, undefined, version
        );

        return this._internalApiRequestService.customOp$(request, this.basePath).pipe(map(() => {}));
    }

    /**
     * Updates an existing campaign with new field values or removes specified fields.
     *
     * Supports partial updates by specifying only the fields to change and
     * field removal by listing field names in removeFields array.
     *
     * @param id The unique identifier of the campaign to update
     * @param version The current version of the campaign for optimistic locking
     * @param fields The campaign fields to update with new values
     * @param removeFields Array of field names to remove from the campaign
     * @returns Observable that emits the updated campaign
     */
    update$(id: string, version: number, fields: Campaign, removeFields?: Array<string>): Observable<Campaign> {

        const request = RequestUtil.createUpdateRequest<Campaign, void>(id, ModelAction.ClientUpdate, fields, version, removeFields);

        return this._internalApiRequestService.update$(request, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Updates the record of the original campaign with the updated model provided
     *
     * @param original The original campaign model
     * @param updated The updated campaign model with changes applied
     * @returns Observable that emits the updated campaign
     */
    updateWithChanges$(original: Campaign, updated: Campaign): Observable<Campaign> {

        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }
}
